<script lang="ts">
  /**
   * The checkpoint banner: the live-task rendering of the timeout checkpoint.
   *
   * "You left off working on: **[Task Title]** on Page N." with the three
   * resolutions, labels verbatim from REPLICATION.md:338:
   *
   *     [ Finished ]  [ Re-enter at End ]  [ Leave Active on Page N ]
   *
   * It authors NOTHING itself — every effect goes out through a callback the
   * host implements, the same rule as the takeover's controls (STEP_6_SPEC.md
   * §2). The degraded renderings (D7 cases 2–3: task settled during the gap,
   * or `current_task_id` null) are NOT this component's: the contract takes a
   * live task only, and the hub renders the degraded note instead. The page
   * number is the task's OWN page, not the session's — self-consistent even
   * when the session's page has drifted (D7 case 1).
   *
   * On mount the heading takes focus, so a keyboard user lands on the
   * checkpoint rather than in a dead document: the hub replaces the stage, so
   * there is nothing else to tab into.
   */
  import Icon from './Icon.svelte';
  import Check from 'lucide/dist/esm/icons/check.mjs';
  import CornerDownRight from 'lucide/dist/esm/icons/corner-down-right.mjs';
  import Pause from 'lucide/dist/esm/icons/pause.mjs';
  import type { Task } from '../types/generated';

  interface Props {
    /** The live checkpoint task (D7 case 1 only). */
    task: Task;
    /** The task's own page. */
    pageNumber: number;
    onFinished: () => void | Promise<void>;
    onReenter: () => void;
    onLeaveActive: () => void;
  }

  let { task, pageNumber, onFinished, onReenter, onLeaveActive }: Props = $props();

  let heading = $state<HTMLElement | null>(null);
  $effect(() => {
    heading?.focus();
  });
</script>

<section
  class="checkpoint-banner"
  data-testid="checkpoint-banner"
  aria-label="Resolve the interrupted task"
>
  <h2 class="checkpoint-heading" data-testid="checkpoint-heading" tabindex="-1" bind:this={heading}>
    You left off working on:
    <strong class="checkpoint-task" data-testid="checkpoint-task">{task.title}</strong>
    on Page {pageNumber}.
  </h2>

  <div class="checkpoint-actions">
    <button class="action" type="button" data-testid="checkpoint-finished" onclick={onFinished}>
      <Icon node={Check} />
      [ Finished ]
    </button>
    <button class="action" type="button" data-testid="checkpoint-reenter" onclick={onReenter}>
      <Icon node={CornerDownRight} />
      [ Re-enter at End ]
    </button>
    <button class="action" type="button" data-testid="checkpoint-leave-active" onclick={onLeaveActive}>
      <Icon node={Pause} />
      [ Leave Active on Page {pageNumber} ]
    </button>
  </div>
</section>

<style>
  .checkpoint-banner {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px 18px;
    border: 1px solid color-mix(in srgb, var(--ink) 16%, transparent);
    border-radius: 4px;
    /* `var(--paper)`, not the `#fdfcf7` literal it replaced: identical in light,
       and it flips to the chalkboard surface in dark. */
    background: var(--paper);
    box-shadow: var(--shadow-card);
    font-family: var(--font-ui);
    font-size: 13px;
    color: var(--ink);
  }

  .checkpoint-heading {
    margin: 0;
    font-size: 14px;
    font-weight: normal;
    /* Focused on mount; the outline would just read as a stray ring over a
       heading the user did not ask to focus. */
    outline: none;
  }

  .checkpoint-task {
    font-weight: bold;
  }

  .checkpoint-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
</style>
